import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronDown, Mail, Pencil, Phone, Plus, Repeat, Users, X } from "lucide-react";
import type { components } from "@cntrl/api-client";
import { Icon, Modal, Picker, Spinner, Tag } from "@cntrl/ui";
import { useIsPro } from "../../app/bandContext.js";
import { publicProfileKey } from "../../app/hooks/useProfile.js";
import { useAvatarUrl } from "../../app/hooks/useAvatar.js";
import {
  useChangeSeatRole,
  useRemoveSeat,
  useSetActingRole,
  useSetCrewParty,
  teamKey,
} from "../../app/hooks/useBandTeam.js";
import { openMessageWith } from "../../app/messagePanel.js";
import { orgInitials } from "../../app/hooks/useOrg.js";
import { bandRoleOptionsFor, pickerRoleLabel, roleLabelFrom, useSeatRoles } from "../../app/hooks/useSeatRoles.js";
import { ConfirmDialog } from "../shared/ConfirmDialog.js";
import { ProfileModal } from "../profile/ProfileModal.js";
// Mutually recursive with BandProfileModal, and that is the feature rather than an accident: a band profile
// opens a person, and a person's band tile opens a band profile. Both sides are hoisted `function`
// declarations used only at render, so the ESM cycle resolves; don't convert either to a `const` arrow.
import { BandProfileBrowser } from "./BandProfileModal.js";
import { RoleLabel } from "./RoleLabel.js";
import { usePersonProfile } from "./usePersonProfile.js";
import { groupLoyalty, loyaltyLabel } from "./travelFormat.js";
import { ACTING_ROLES, ActingRoleFullLabel } from "./CrewSeatRow.js";
import { CREW_PARTIES, crewPartyLabel } from "./crewParty.js";
import styles from "./CrewProfileModal.module.scss";
import { partnerRelationshipLabel } from "../../app/partnerRelationship.js";

/**
 * A person's profile — the screen you get by tapping a row on Your Crew, a member row on a band profile,
 * a teammate on the org Team list, or "View my profile" in any shell's account menu.
 *
 * **A headerless dialog, not a new primitive.** `Modal` already supports `hideHeader` (the title survives as
 * an sr-only label so `aria-labelledby` still resolves), which is how the sibling `BandProfileModal` is
 * built. Hand-rolling a dialog would cost the focus trap, the Escape *stack* and the body-scroll lock.
 *
 * **The rail is identity, the pane is content.** Left: the photo hero (the person's picture as the top
 * square's background), the organizations and bands this person and the viewer share (tiles), then contact.
 * Right: bio, skills, and the money-team-gated travel block.
 *
 * **The pills ARE the dropdowns** (the reference design; PO, 2026-08-20). For a TM/AV viewer the hero's
 * seat pill and acting pill are `Picker` chip triggers — there is no separate labelled controls section.
 * Everyone else sees the same pills as static facts.
 *
 * **Two roles, never merged.** The seat role is membership and carries every permission this person has. The
 * acting role is a label: "covering tour management this run". Rendering them as one pill would either imply
 * a grant nobody made, or lose the distinction the tour actually runs on.
 *
 * **It is addressed by PERSON, and it resolves the rest itself (2026-09-01).** This is the ONE person dialog:
 * your own profile and a colleague's are the same modal, and the only difference between them is the Edit
 * pencil. It used to take a `BandSeatView` plus `isSelf` plus `canEditRoles`, which meant only a caller
 * already holding a seat could open it — so the band profile's member rows opened the plain `ProfileModal`
 * instead, and the same person read as two unrelated dialogs depending on which door you came through (a
 * 780x592 identity rail from the crew card, a 520x276 card from the band profile).
 *
 * Resolving here rather than at the call site is what actually makes them one modal: `isSelf` and
 * `canEditRoles` were derived identically by both callers, and a third door deriving them slightly
 * differently is how a dialog silently becomes two again. A person with NO seat in the active band — a friend
 * band's member — is a real, expected state and renders the same dialog with the seat controls absent.
 *
 * **It renders with NO BAND MOUNTED AT ALL, and that is what gives an ORG member the same profile capacity a
 * band member has (PO, 2026-09-01).** The roster and org shells have no `BandProvider`, so every hook here
 * that reached for the THROWING band context had to become context-safe — `useBandTeamOrNull`,
 * `useFriendBandIds`, `useBandContextOrNull` — and the three seat MUTATIONS, which cannot be, moved into
 * child components that only mount once a seat has been resolved (which can only happen inside a band). The
 * alternative was a second org-side person dialog, and a second dialog is exactly what the paragraph above
 * exists to prevent. "No band mounted" and "no seat in the mounted band" then collapse to one code path,
 * because they are the same thing from this surface's point of view: there is no seat to render or edit.
 */
export function CrewProfileModal({
  personId,
  fallback,
  bandBackdrop,
  onClose,
}: {
  /** WHO. The seat, `isSelf` and the edit rights are all resolved from this. */
  personId: string;
  /**
   * What the opening surface already knows, so the hero paints before the reads land — and so it still
   * reads right for a person with no seat here, where there is nothing to resolve. A resolved seat WINS:
   * this is a head start, never a second source of truth.
   */
  fallback?: { name?: string | null; role?: string | null; isOwner?: boolean };
  /**
   * The band the viewer navigated FROM (a band-profile browse hands it in), used as the hero backdrop when
   * the person has no picture of their own. It is needed because the shared-bands list is EMPTY for a
   * friend band's member by design (a friendship between bands is not a shared band), so without it there is
   * nothing to blur behind a friend's crew. Other doors (crew card, org Team, account menu) pass none and
   * fall to the person's first shared band, unchanged.
   */
  bandBackdrop?: { bandId: string; avatarMediaId: string | null };
  onClose: () => void;
}) {
  // The person's identity, permissions and hero-backdrop chain are resolved ONCE in `usePersonProfile`,
  // shared with the mobile `MobileContactProfileScreen` (ART-194) — destructured back into the same names
  // the render below uses, so this modal's markup is unchanged. Only the UI state (editing / confirming /
  // browsing) stays here; those are chrome, not facts.
  const {
    profile, travel, payment, activeBandId,
    seat, isSelf, name, role, titleKey, isOwner, friendBandIds,
    canMessage, isPro, canEditRoles,
    seatId, canEditSeatRole, canEditActing, canEditParty, removable,
    heroUrl, heroIsBandBackdrop,
    orgItems, bandItems, labelStrips,
  } = usePersonProfile(personId, fallback, bandBackdrop);

  const [confirming, setConfirming] = useState(false);
  const [editing, setEditing] = useState(false);
  // A band tile opens that band's PROFILE where the viewer has reach: the active band always, a friend
  // band via the friends list. Unreachable bands — and every band tile under a shell with no band, where
  // the friend browse has no "us" to browse from — stay display-only tiles.
  const [browsingBand, setBrowsingBand] = useState<string | null>(null);
  const qc = useQueryClient();

  // Closing the editor refreshes what it may have changed: the public profile (bio/skills/avatar on
  // this very modal) and the crew read (displayName feeds the hero + the row behind it). The editor
  // itself already reseeds the me-profile cache on save.
  const closeEditor = () => {
    setEditing(false);
    void qc.invalidateQueries({ queryKey: publicProfileKey(personId) });
    if (activeBandId) void qc.invalidateQueries({ queryKey: teamKey(activeBandId) });
  };

  return (
    <Modal title={name} onClose={onClose} closeLabel="Close profile" size="xl" hideHeader bodyLayout="flush">
      <div className={styles.split}>
        <aside className={styles.rail}>
          {/* The rail's top square: photo (or blurred band backdrop) behind, identity bottom-aligned. */}
          <div className={heroUrl ? styles.hero : `${styles.hero} ${styles.heroBare}`}>
            <button type="button" className={styles.close} aria-label="Close profile" onClick={onClose}>
              <Icon icon={X} size={20} />
            </button>
            {/* Self only: the person-record editor (the same form the routed /profile serves). Sits to
                the RIGHT of close; the stacked Modal unwinds one Escape at a time like every overlay. */}
            {isSelf && (
              <button type="button" className={styles.edit} aria-label="Edit profile" onClick={() => setEditing(true)}>
                <Icon icon={Pencil} size={16} />
              </button>
            )}

            {/* No initials fallback here (PO, 2026-08-20): the photo is the hero's BACKGROUND, so when
                neither picture exists the rail simply stays ink — the name is the identity, not a monogram. */}
            {heroUrl && (
              <img
                className={heroIsBandBackdrop ? `${styles.heroPhoto} ${styles.heroPhotoBlur}` : styles.heroPhoto}
                src={heroUrl}
                alt=""
                data-photo=""
              />
            )}

            <div className={styles.heroText}>
              <h2 className={styles.heroName}>{name}</h2>

              {/* The seat pill. For a TM/AV viewer it IS the crew-position dropdown (the reference
                  design — no separate labelled control). The owner seat and your own seat stay static
                  facts: the gate refuses both changes, and a control that always 403s is worse than none. */}
              {canEditSeatRole && seatId ? (
                <SeatRolePill seatId={seatId} role={role} titleKey={titleKey} />
              ) : (
                role != null && (
                  <span
                    className={styles.seatPill}
                    title={
                      canEditRoles && seat
                        ? isOwner
                          ? "The owner seat changes only through ownership transfer."
                          : "You can't change your own position."
                        : undefined
                    }
                  >
                    <RoleLabel role={role} titleKey={titleKey} owner={isOwner} variant="light" />
                  </span>
                )
              )}

              {/* The third pill, and ART-45 is the ternary choosing WHICH: Pro offers the touring PARTY,
                  Standard offers "Acting as". One decision, in one place. `acting_role` and every stored
                  value are untouched either way — a Pro band's acting labels are not cleared, merely not
                  offered, so an upgrade loses nothing and a downgrade restores the affordance over data that
                  was still there. Both grant nothing.

                  For the party the empty value IS "reset to default", exactly as the empty acting value is
                  "clear role" — one call, not a second action. */}
              {isPro ? (
                canEditParty && seatId ? (
                  <CrewPartyPill seatId={seatId} party={seat?.party ?? null} />
                ) : (
                  seat?.party && (
                    <span className={styles.actingPill}>
                      <Icon icon={Users} size={16} />
                      {crewPartyLabel(seat.party)}
                    </span>
                  )
                )
              ) : canEditActing && seatId ? (
                <ActingRolePill seatId={seatId} actingRole={seat?.actingRole ?? null} />
              ) : (
                seat?.actingRole && (
                  <span className={styles.actingPill}>
                    <Icon icon={Repeat} size={16} />
                    <ActingRoleFullLabel role={seat.actingRole} />
                  </span>
                )
              )}
            </div>
          </div>

          <div className={styles.railBody}>
            {/* The ORGANIZATIONS this person and the viewer both sit in. First, because for an org seat
                this IS their identity — the artists below are what that seat reaches. */}
            {orgItems.length > 0 && (
              <div className={styles.strip}>
                {labelStrips && <p className={styles.stripLabel}>Organizations</p>}
                <ul className={styles.bandStrip} aria-label={`Organizations you share with ${name}`}>
                  {orgItems.map((o) => (
                    <OrgTile key={o.orgId} org={o} />
                  ))}
                </ul>
              </div>
            )}

            {bandItems.length > 0 && (
              <div className={styles.strip}>
                {labelStrips && <p className={styles.stripLabel}>Artists</p>}
                <ul className={styles.bandStrip} aria-label={`Bands you share with ${name}`}>
                  {bandItems.map((b) => (
                    <BandTile
                      key={b.bandId}
                      band={b}
                      active={!!activeBandId && b.bandId === activeBandId}
                      onOpen={
                        b.bandId && (b.bandId === activeBandId || friendBandIds.has(b.bandId))
                          ? () => setBrowsingBand(b.bandId ?? null)
                          : undefined
                      }
                    />
                  ))}
                </ul>
              </div>
            )}

            {/* The bottom "reach" section (screenshot, 2026-09-14): a prominent Message action, the contact
                details, and the sometimes-present destructive control, grouped under one divider and pinned
                to the foot of the rail. Rendered only when it would carry SOMETHING — an empty divider is a
                line to nowhere. */}
            {(profile.data?.location || profile.data?.partner || canMessage || seat?.email || profile.data?.phone || removable) && (
              <div className={styles.reach}>
                {profile.data?.location && <p className={styles.meta}>{profile.data.location}</p>}
                {/* The member's partner (ART-239) — name only, and only for their band: the server fills it for a viewer
                    seated on one of their bands (or the member) and nulls it for everyone else. */}
                {profile.data?.partner && <p className={styles.meta}>{partnerRelationshipLabel(profile.data.partnerRelationship) ?? "Partner"} · {profile.data.partner}</p>}

                {/* MESSAGE (ART-159, C-Entry). It leads because it is the one way to reach this person that
                    stays inside the product, and it is the filled pill for the same reason.

                    **Offered only when the server says they are reachable AND we can message at all.**
                    `useMessageable` IS the create gate read forwards, so an offered action is always an
                    accepted one, and `hasInbox` keeps it off an artist/owner account (see `canMessage`
                    above). It also covers `isSelf` for free: you are never in your own messageable list. */}
                {canMessage && (
                  <button
                    type="button"
                    className={styles.messageButton}
                    onClick={() => {
                      // The dialog CLOSES: the inbox is an overlay too, and leaving this one underneath it
                      // would stack two dialogs on one subject with the lower one unreachable.
                      onClose();
                      openMessageWith(personId);
                    }}
                  >
                    Message
                  </button>
                )}

                {(profile.data?.phone || seat?.email) && (
                  <div className={styles.details}>
                    {/* Phone is COLLEAGUE-only (PO, 2026-08-20 for a shared band, extended 2026-09-01 to a
                        shared organization — a shared employer is the same class of relationship): the
                        server fills it for a shared-band or shared-org viewer and nulls it for friend-band
                        reach, so rendering-when-present is the whole client rule. */}
                    {profile.data?.phone && (
                      <a className={styles.link} href={`tel:${profile.data.phone}`}>
                        <Icon icon={Phone} size={16} />
                        {profile.data.phone}
                      </a>
                    )}
                    {seat?.email && (
                      <a className={styles.link} href={`mailto:${seat.email}`}>
                        <Icon icon={Mail} size={16} />
                        {seat.email}
                      </a>
                    )}
                  </div>
                )}

                {removable && (
                  <button type="button" className={styles.removeButton} onClick={() => setConfirming(true)}>
                    Remove from band
                  </button>
                )}
              </div>
            )}
          </div>
        </aside>

        <section className={styles.body}>
          {profile.isLoading ? (
            <div className={styles.center} role="status" aria-live="polite">
              <Spinner size={22} label="Loading profile…" />
            </div>
          ) : (
            <>
              {profile.data?.bio && (
                <div className={styles.section}>
                  <p className={styles.sectionLabel}>Bio</p>
                  <p className={styles.bio}>{profile.data.bio}</p>
                </div>
              )}
              {(profile.data?.skills?.length ?? 0) > 0 && (
                <div className={styles.section}>
                  <p className={styles.sectionLabel}>Skills</p>
                  <div className={styles.chips}>
                    {profile.data?.skills?.map((sk) => (
                      <Tag key={sk} tone="muted">
                        {sk}
                      </Tag>
                    ))}
                  </div>
                </div>
              )}
              {/* Travel is its own gated read — the money team only. Absent means "not permitted", and the
                  right response to that is silence, not a locked-looking placeholder. */}
              {travel.data && (() => {
                // Grouped by the shared helper, so this modal and the mobile card cannot split the loyalty
                // list two ways. Entries within a group join with a comma; name·number stays the middot.
                const { airlines, hotels } = groupLoyalty(travel.data.loyaltyPrograms);
                const empty =
                  !travel.data.knownTravelerNumber &&
                  !travel.data.redressNumber &&
                  airlines.length === 0 &&
                  hotels.length === 0;
                return (
                  <div className={styles.section}>
                    <p className={styles.sectionLabel}>Travel &amp; Loyalty</p>
                    {travel.data.knownTravelerNumber && (
                      <p className={styles.meta}>Known Traveler · {travel.data.knownTravelerNumber}</p>
                    )}
                    {travel.data.redressNumber && (
                      <p className={styles.meta}>Redress · {travel.data.redressNumber}</p>
                    )}
                    {airlines.length > 0 && (
                      <p className={styles.meta}>Airlines · {airlines.map(loyaltyLabel).join(", ")}</p>
                    )}
                    {hotels.length > 0 && (
                      <p className={styles.meta}>Hotels · {hotels.map(loyaltyLabel).join(", ")}</p>
                    )}
                    {empty && <p className={styles.meta}>Nothing recorded yet.</p>}
                  </div>
                );
              })()}
              {/* Payment — the same money-team gated read as Travel (ART-241). Its own section beside Travel;
                  the handle is shown WITH the leading '@' for readability but stored without it. */}
              {payment.data && (
                <div className={styles.section}>
                  <p className={styles.sectionLabel}>Payment</p>
                  {payment.data.venmoUsername ? (
                    <p className={styles.meta}>Venmo · @{payment.data.venmoUsername}</p>
                  ) : (
                    <p className={styles.meta}>No Venmo on profile.</p>
                  )}
                </div>
              )}
            </>
          )}
        </section>
      </div>

      {editing && <ProfileModal onClose={closeEditor} />}

      {browsingBand && <BandProfileBrowser initialBandId={browsingBand} onClose={() => setBrowsingBand(null)} />}

      {/* Stays a direct child of the Modal: ConfirmDialog is `position: fixed` rather than portaled, so
          moving it down beside its trigger would put it inside the rail's scroll container. */}
      {confirming && seatId && (
        <RemoveSeatConfirm
          seatId={seatId}
          name={name}
          onCancel={() => setConfirming(false)}
          onRemoved={() => {
            setConfirming(false);
            onClose();
          }}
        />
      )}
    </Modal>
  );
}

// ---- the seat controls ------------------------------------------------------------------------------
//
// All three hold a band-scoped MUTATION, which reads the THROWING band context — so each is a component
// rather than a hook call in the parent, and each mounts only once a seat has resolved. A resolved seat
// implies a mounted band, so the throwing context can never fire; a hook called unconditionally in the
// parent would crash this dialog everywhere it now opens without one (the roster Team list, the org
// account menu). Splitting them out is the only shape that keeps ONE person dialog across all four shells.

/** How a role pill is dressed — the dialog's dark identity rail (the default) or the phone profile's light
 *  canvas (ART-251). The PICKER logic is shared; only the chrome differs per surface. */
export interface RolePillSkin {
  seat?: string;
  acting?: string;
  actingGhost?: string;
  /** The seat label's variant: "light" text for the dark rail, the default for a light canvas. */
  roleVariant?: "light";
}

const RAIL_SKIN: RolePillSkin = {
  seat: styles.seatPill, acting: styles.actingPill, actingGhost: styles.actingPillGhost, roleVariant: "light",
};

/** The crew-position pill AS the dropdown (the reference design) — a TM/AV viewer, someone else's seat. Shared
 *  with the phone's contact profile (ART-251), which passes its own `skin`. */
export function SeatRolePill(
  { seatId, role, titleKey, skin = RAIL_SKIN }:
  { seatId: string; role: string | null; titleKey: string | null; skin?: RolePillSkin },
) {
  const changeRole = useChangeSeatRole();
  const seatRoles = useSeatRoles();
  const isPro = useIsPro();
  // ⚠️ THE OPTIONS ARE TITLES, FROM THE SERVER. They were a hard-coded BAND_ROLES array of ROLE keys, and
  // the titles split (798/799) made five of its eight values invalid — `core.change_band_seat_role` takes a
  // TITLE and resolves the role itself, so picking "Band Member" or any production seat 422'd. The mutation
  // below already said `title:`; only the list it was fed had not caught up.
  //
  // ART-150: a Standard band is not offered the 15 Pro crew titles — but a band that DOWNGRADED keeps any Pro
  // seat it already holds (gate the picker, never the vocabulary), so the seat's CURRENT title stays in the
  // list even when it is Pro, or the Picker could not render its own value. The band seat gate is the server
  // backstop; re-picking the same Pro title is a no-op, and no OTHER Pro title is offered.
  const all = seatRoles.data?.bandRoles ?? [];
  const filtered = bandRoleOptionsFor(all, isPro);
  const options = titleKey && !filtered.some((r) => r.role === titleKey)
    ? [...filtered, ...all.filter((r) => r.role === titleKey)]
    : filtered;
  return (
    <Picker
      label="Crew position"
      value={titleKey ?? ""}
      disabled={changeRole.isPending || options.length === 0}
      options={options.map((r) => ({
        value: r.role ?? "",     // SeatRoleOption.role carries the TITLE key (roleKey is the role it grants)
        // ART-171: OPTIONS spell out full access — you are choosing between the full-access and limited
        // "Band Member" titles, where the mark alone is too quiet. The trigger below is a DISPLAY of the
        // current role, so it stays "Band Member" + mark like every other row.
        label: pickerRoleLabel(r),
        render: <RoleLabel role={r.roleKey} titleKey={r.role} spellFullAccess />,
      }))}
      renderTrigger={() => (
        <span className={skin.seat}>
          <RoleLabel role={role} titleKey={titleKey} variant={skin.roleVariant} />
          <Icon icon={ChevronDown} size={16} />
        </span>
      )}
      onChange={(next) => changeRole.mutate({ seatId, title: next })}
    />
  );
}

/** The acting-role pill. The empty value IS "clear role" — one call, not a second action. Grants nothing. */
export function ActingRolePill(
  { seatId, actingRole, skin = RAIL_SKIN }: { seatId: string; actingRole: string | null; skin?: RolePillSkin },
) {
  const setActing = useSetActingRole();
  return (
    <Picker
      label="Acting as"
      value={actingRole ?? ""}
      disabled={setActing.isPending}
      options={[
        { value: "", label: "No acting role" },
        ...ACTING_ROLES.map((r) => ({ value: r.role, label: r.label })),
      ]}
      renderTrigger={() =>
        actingRole ? (
          <span className={skin.acting}>
            <Icon icon={Repeat} size={16} />
            <ActingRoleFullLabel role={actingRole} />
            <Icon icon={ChevronDown} size={16} />
          </span>
        ) : (
          <span className={`${skin.acting} ${skin.actingGhost}`}>
            <Icon icon={Plus} size={16} />
            Acting role
          </span>
        )
      }
      onChange={(v) => setActing.mutate({ seatId, actingRole: v || null })}
    />
  );
}

/**
 * The party pill AS the dropdown — the acting pill's shape, with one difference that matters: a seat ALWAYS
 * has a party (the C-Party floor), so there is no ghost "+" state and the trigger is never empty.
 *
 * "Reset to default" is the empty value, and it is offered UNCONDITIONALLY rather than only when an override
 * exists. The alternative reads better and is worse: the row does not say which of the five is the title's
 * default, so a reader who cannot see the option cannot tell whether they are already on it.
 */
function CrewPartyPill({ seatId, party }: { seatId: string; party: string | null }) {
  const setParty = useSetCrewParty();
  return (
    <Picker
      label="Party"
      value={party ?? ""}
      disabled={setParty.isPending}
      options={[
        ...CREW_PARTIES.map((p) => ({ value: p.key, label: p.label })),
        { value: "", label: "Reset to default" },
      ]}
      renderTrigger={() => (
        <span className={styles.actingPill}>
          <Icon icon={Users} size={16} />
          {crewPartyLabel(party)}
          <Icon icon={ChevronDown} size={16} />
        </span>
      )}
      onChange={(v) => setParty.mutate({ seatId, party: v || null })}
    />
  );
}

/** The destructive-confirm over the seat removal. Surfaces the gate's refusal verbatim and stays open on it. */
function RemoveSeatConfirm({
  seatId,
  name,
  onCancel,
  onRemoved,
}: {
  seatId: string;
  name: string;
  onCancel: () => void;
  onRemoved: () => void;
}) {
  const remove = useRemoveSeat();
  const [error, setError] = useState<string | null>(null);
  return (
    <ConfirmDialog
      title={`Remove ${name}?`}
      description="They lose access to this band immediately. Past data stays."
      confirmLabel="Remove member"
      error={error}
      loading={remove.isPending}
      onConfirm={() => {
        setError(null);
        remove.mutate(seatId, { onSuccess: onRemoved, onError: (err) => setError(err.message) });
      }}
      onCancel={onCancel}
    />
  );
}

// ---- rail tiles -------------------------------------------------------------------------------------

/**
 * One shared organization on the rail — initials (orgs carry no photo anywhere in the product), the org's
 * name, and the person's ROLE there, or "Sees all artists" for an admin whose reach is the whole roster.
 *
 * Display-only, and deliberately so: the band tile beside it opens a band PROFILE, and there is no org
 * equivalent to open — an organization's public face is not a surface this product has. A tile that looks
 * clickable and is not would be worse than one that plainly is not.
 */
function OrgTile({ org }: { org: components["schemas"]["PersonOrgView"] }) {
  // Person-scoped (`/me/seat-roles`), so it resolves here even though this dialog mounts with NO band and
  // no org — which is exactly why the catalog is not served from a tenant route.
  const seatRoles = useSeatRoles();
  return (
    <li>
      <div className={styles.bandTile}>
        <span className={styles.bandTilePhoto} aria-hidden="true">
          {orgInitials(org.name ?? "")}
        </span>
        <span className={styles.bandTileName}>{org.name}</span>
        <span className={styles.bandTileRole}>
          {org.seesAllBands ? "Sees all artists" : roleLabelFrom(seatRoles.data?.orgRoles, org.titleKey)}
        </span>
      </div>
    </li>
  );
}

/**
 * One shared band on the rail — the band's MAIN photo (the same `avatarMediaId` mirror the shell's band
 * switcher resolves; the media route is band-scoped, and a shared band is by definition one the viewer
 * reaches), the band's name, and how the person reaches it. The band this modal was opened FROM gets
 * the accent ring. With `onOpen` (the viewer has reach: the active band, or a friend band) the tile is a
 * button that opens that band's PROFILE; without it, a display-only tile.
 *
 * **`access` decides which role vocabulary the tile speaks**, and the row carries it rather than the tile
 * guessing: a `band_seat` row's role is a `core.band_seats` key ("tour_manager") and goes through
 * `RoleLabel`, which owns the AV mark and the acronym seats; an `org_reach` row's is a `core.org_seats` one
 * ("booking_agent") and is resolved against the ORG list of the served catalog. The two sets are disjoint
 * but not visibly so, and running one through the other's list title-cases it into something plausible and
 * wrong — which is why `/me/seat-roles` returns them as two arrays rather than one merged map.
 */
export function BandTile({
  band,
  active,
  onOpen,
}: {
  band: components["schemas"]["PersonBandView"];
  active: boolean;
  onOpen?: () => void;
}) {
  const seatRoles = useSeatRoles();
  // A band picture is public identity — the global avatar route serves it for any band, reachable or not
  // (a band-scoped media route 403s for a shared band the viewer has no seat on). The one band-photo path.
  const photoUrl = useAvatarUrl("band", band.bandId, band.avatarMediaId);
  const face = (
    <>
      {photoUrl ? (
        <img className={styles.bandTilePhoto} src={photoUrl} alt="" data-photo="" />
      ) : (
        <span className={styles.bandTilePhoto} aria-hidden="true">
          {orgInitials(band.name ?? "")}
        </span>
      )}
      <span className={styles.bandTileName}>{band.name}</span>
      {band.access === "org_reach" ? (
        <span className={styles.bandTileRole}>{roleLabelFrom(seatRoles.data?.orgRoles, band.titleKey)}</span>
      ) : (
        /* Default (dark) variant on purpose: the text inherits the rail's paper, and the MARK stays
           crimson — the reference tile shows the red mark even on ink. */
        <RoleLabel role={band.role} owner={band.isOwner} className={styles.bandTileRole} />
      )}
    </>
  );
  return (
    <li data-active={active || undefined}>
      {onOpen ? (
        <button
          type="button"
          className={active ? `${styles.bandTile} ${styles.bandTileButton} ${styles.bandTileActive}` : `${styles.bandTile} ${styles.bandTileButton}`}
          aria-label={`View ${band.name ?? "band"}'s profile`}
          onClick={onOpen}
        >
          {face}
        </button>
      ) : (
        <div className={active ? `${styles.bandTile} ${styles.bandTileActive}` : styles.bandTile}>{face}</div>
      )}
    </li>
  );
}
